import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import CardContent from '@mui/material/CardContent'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import InputAdornment from '@mui/material/InputAdornment'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SearchIcon from '@mui/icons-material/Search'
import type { AddBookState } from '../../hooks/useAddBook'
import ErrorMessage from '../ErrorMessage'
import { PremiumChip, UpgradeDialog } from '../Premium'

export default function CatalogSearchStep({ ab }: { ab: AddBookState }) {
  const navigate = useNavigate()
  const {
    query, setQuery, searching, searchError, selectedBook, setSelectedBook, setTitle, setAuthor, setMode, results, submitting, error, isFree, upgradeOpen, setUpgradeOpen, openUpgrade, retrySearch, handleSelectBook, handleGenerateQuestions, setPdfFile, setKoboFile, setKoboResult, setKindleFile, setKindleResult, setReadwiseFile, setReadwiseResult, setError,
  } = ab

  return (
      <Box sx={{ maxWidth: 600, mx: 'auto' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/library')} sx={{ mb: 2 }}>
          Back to Library
        </Button>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>Add Book</Typography>

        <TextField
          fullWidth
          placeholder="Search by title..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          autoFocus
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
              endAdornment: searching ? <InputAdornment position="end"><CircularProgress size={18} /></InputAdornment> : null,
            },
          }}
          sx={{ mb: 2 }}
        />

        {searchError && !selectedBook && (
          <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: 'center' }}>
            <Typography variant="body2" color="error">{searchError}</Typography>
            <Button size="small" onClick={retrySearch}>Try again</Button>
          </Stack>
        )}

        {selectedBook && (
          <Card elevation={1} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                {selectedBook.cover_i && (
                  <Box
                    component="img"
                    src={`https://covers.openlibrary.org/b/id/${selectedBook.cover_i}-M.jpg`}
                    alt=""
                    sx={{ width: 60, height: 90, objectFit: 'cover', borderRadius: 1 }}
                    onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                  />
                )}
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>{selectedBook.title}</Typography>
                  {selectedBook.author_name?.[0] && <Typography variant="body2" color="text.secondary">{selectedBook.author_name[0]}</Typography>}
                  {selectedBook.first_publish_year && <Typography variant="caption" color="text.secondary">{selectedBook.first_publish_year}</Typography>}
                </Box>
              </Box>
              {error && <ErrorMessage message={error} />}
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={handleGenerateQuestions} disabled={submitting}>
                  {/* <span> wrapper: Chrome's translator wraps text nodes in <font>, and swapping
                      to/from the spinner element leaves React holding a stale node reference. */}
                  {submitting ? <CircularProgress size={18} /> : <span>Generate Questions</span>}
                </Button>
                <Button
                  variant="text"
                  onClick={() => { if (isFree) { openUpgrade('import'); return } setMode('highlights') }}
                  endIcon={isFree ? <PremiumChip /> : undefined}
                >
                  Add my highlights →
                </Button>
              </Stack>
            </CardContent>
          </Card>
        )}

        {results.length > 0 && !selectedBook && (
          <Card elevation={1} sx={{ mb: 2 }}>
            <List disablePadding>
              {results.map((book, i) => (
                <Box key={book.key}>
                  {i > 0 && <Divider />}
                  <CardActionArea onClick={() => handleSelectBook(book)}>
                    <ListItem>
                      {book.cover_i ? (
                        <Box
                          component="img"
                          src={`https://covers.openlibrary.org/b/id/${book.cover_i}-M.jpg`}
                          alt=""
                          sx={{ width: 40, height: 56, objectFit: 'cover', borderRadius: 0.5, mr: 2, flexShrink: 0 }}
                          onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                        />
                      ) : (
                        <Box sx={{ width: 40, height: 56, bgcolor: 'action.hover', borderRadius: 0.5, mr: 2, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          📖
                        </Box>
                      )}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{book.title}</Typography>
                        {book.author_name?.[0] && <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{book.author_name[0]}</Typography>}
                      </Box>
                      {book.first_publish_year && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', flexShrink: 0 }}>{book.first_publish_year}</Typography>
                      )}
                    </ListItem>
                  </CardActionArea>
                </Box>
              ))}
            </List>
          </Card>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          For best results, import your own Kindle highlights — they produce questions tailored to what you actually read.
        </Typography>

        <Divider sx={{ mb: 2 }}><Typography variant="caption" color="text.secondary">or</Typography></Divider>

        <Stack spacing={1}>
          <Button variant="text" onClick={() => { setSelectedBook(null); setTitle(''); setAuthor(''); setMode('manual') }}>
            Enter title &amp; author manually →
          </Button>
          <Button
            variant="text"
            onClick={() => { if (isFree) { openUpgrade('import'); return } setTitle(''); setAuthor(''); setPdfFile(null); setError(null); setMode('pdf') }}
            endIcon={isFree ? <PremiumChip /> : undefined}
          >
            Import from PDF →
          </Button>
          <Button
            variant="text"
            onClick={() => { if (isFree) { openUpgrade('import'); return } setKoboFile(null); setKoboResult(null); setError(null); setMode('kobo') }}
            endIcon={isFree ? <PremiumChip /> : undefined}
          >
            Import Kobo highlights →
          </Button>
          <Button
            variant="text"
            onClick={() => { if (isFree) { openUpgrade('import'); return } setKindleFile(null); setKindleResult(null); setError(null); setMode('kindle-clippings') }}
            endIcon={isFree ? <PremiumChip /> : undefined}
          >
            Import Kindle device highlights (My Clippings.txt) →
          </Button>
          <Button
            variant="text"
            onClick={() => { if (isFree) { openUpgrade('import'); return } setReadwiseFile(null); setReadwiseResult(null); setError(null); setMode('readwise') }}
            endIcon={isFree ? <PremiumChip /> : undefined}
          >
            Import from Readwise →
          </Button>
        </Stack>

        <UpgradeDialog
          open={upgradeOpen}
          onClose={() => setUpgradeOpen(false)}
          title="Imports are part of Premium"
          feature="Kindle, Kobo, Readwise & PDF imports"
        />
      </Box>
  )
}
